// @flow
import classnames from 'classnames';

import { shapeNames } from '@src/utils/displayMaps';
import { fullSizeShapeImagePath } from '@src/utils/images';

import DiamondDetail from '@components/dyor/DiamondDetail';
import FormattedPrice from '@components/FormattedPrice';

import * as routes from '@src/app_routes/diamonds';

import css from '@styles/dyor';

import type { DyorDiamondType } from '@types/diamond';

type SpecsPropsType = {
  diamond: DyorDiamondType
};

type ImagePropsType = {
  diamond: DyorDiamondType
};

type EmptyPropsType = {
  active: boolean,
  setVisibleDetails: (string | null) => void
};

type PropsType = {
  diamond: ?DyorDiamondType,
  active: boolean,
  detailsAreVisible: boolean,
  setVisibleDetails: (string | null) => void,
  containerRef: { current: null | React.ElementRef<'div'> },
  chainPosition: number
};

const placeholderDiamond = '/images/ring-builder/diamond.png';

function EmptyDiamond ({ active, setVisibleDetails }: EmptyPropsType) {
  const onClick = React.useCallback(
    () => {
      setVisibleDetails(null);
      if (!window) return;
      if (window.location.pathname !== routes.searchPath()) {
        window.location = routes.searchPath();
      }
    },
    [active, setVisibleDetails]
  );

  return (
    <section
      className={classnames(css.section, 'empty', { active })}
      onClick={onClick}>
      <PlaceholderImage />
      <div className={css.specifications}>
        <h3 className={css.sectionHeader}>
          <span className={css.chooseText}>Choose </span>
          <span className={css.chooseAText}>a </span>Diamond
        </h3>
        {active && <p className={css.sectionCaption}>Select your diamond</p>}
      </div>
      <div
        className={classnames({
          [css['blueChevron']]: active,
          [css['greyChevron']]: !active
        })}
      />
    </section>
  );
}

function DiamondImage ({ diamond }: ImagePropsType) {
  return (
    <div className={css.imageContainer}>
      <img src={fullSizeShapeImagePath(diamond)} alt="Diamond image" />
    </div>
  );
}

function PlaceholderImage () {
  return (
    <div className={css.imageContainer}>
      <img src={placeholderDiamond} alt="Placeholder diamond image" />
    </div>
  );
}

function DiamondSpecifications ({ diamond }: SpecsPropsType) {
  const { carat } = diamond;
  const shape = shapeNames[diamond.shape];
  const price = <FormattedPrice value={diamond.retail_price} />;

  return (
    <div className={css.specifications}>
      <h3 className={css.sectionHeader}>
        <span className={css.sectionPronoun}>Your </span>Diamond
      </h3>
      <p className={css.sectionCaption}>
        {carat} {shape}
        <span className={css.sectionPrice}> - {price}</span>
      </p>
    </div>
  );
}

export default function Diamond ({
  diamond,
  active,
  detailsAreVisible,
  setVisibleDetails,
  containerRef,
  chainPosition
}: PropsType) {
  const detailsRef = React.useRef(null);
  const tileRef = React.useRef(null);

  const toggleDetails = React.useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!(e.target instanceof Node)) return;
      if (detailsRef.current && detailsRef.current.contains(e.target)) return;
      if (!containerRef.current) return;
      if (!tileRef.current) return;
      if (
        containerRef.current.contains(e.target) &&
        !tileRef.current.contains(e.target)
      ) {
        return;
      }

      e.stopPropagation();

      setVisibleDetails(detailsAreVisible ? null : 'diamond');

    },
    [containerRef, detailsAreVisible, setVisibleDetails]
  );

  React.useEffect(
    () => {
      if (document) {
        if (detailsAreVisible) {
          if (window.PointerEvent) {
            document.addEventListener('pointerup', toggleDetails);
          } else {
            document.addEventListener('click', toggleDetails);
            document.addEventListener('touchend', toggleDetails);
          }
        } else {
          if (window.PointerEvent) {
            document.removeEventListener('pointerup', toggleDetails);
          } else {
            document.removeEventListener('click', toggleDetails);
            document.removeEventListener('touchend', toggleDetails);
          }
        }
      }

      return () => {
        if (document) {
          if (window.PointerEvent) {
            document.removeEventListener('pointerup', toggleDetails);
          } else {
            document.removeEventListener('click', toggleDetails);
            document.removeEventListener('touchend', toggleDetails);
          }
        }
      };
    },
    [detailsAreVisible, toggleDetails]
  );

  if (!diamond) {
    return (
      <EmptyDiamond active={active} setVisibleDetails={setVisibleDetails} />
    );
  }

  return (
    <section
      className={classnames(css.section, { active })}
      onPointerUp={toggleDetails}
      onClick={() => {
        // We still support onclick for old browsers that don't have PointerEvent. We don't want to call toggleDetails
        // for both events though
        if (window.PointerEvent) return;

        return toggleDetails();
      }}
      ref={tileRef}>
      <DiamondImage diamond={diamond} />
      <DiamondSpecifications diamond={diamond} />
      <DiamondDetail
        diamond={diamond}
        show={detailsAreVisible}
        ref={detailsRef}
        chainPosition={chainPosition}
        containerRef={containerRef}
      />
      <div
        className={classnames({
          [css['blueChevron']]: active,
          [css['greyChevron']]: !active
        })}
      />
    </section>
  );
}
